/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Heading, HFlow, Icon, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { CheckboxField, resolveName } from 'components/form'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'
import { PecTag } from 'view/atendimentos/list/PecTag'

import { MChatRfModalButtonClose } from '../../components/MChatRfModalButtonClose'
import { MCHAT_PERGUNTAS } from '../../mchat/model-mchat'
import { getMChatPerguntaNumber } from '../../mchat/utils-mchat'
import { ConsultaSeguimentoFieldModel, ConsultaSeguimentoSelectionMode } from '../model-consultaSeguimento'

interface ConsultaSeguimentoModalTabSelecaoProps {
  path: MetaRoot<ConsultaSeguimentoFieldModel>
  perguntasFalhasNoMChat: MChatPerguntaEnum[]
  onClose: () => void
}

export function ConsultaSeguimentoModalTabSelecao(props: Readonly<ConsultaSeguimentoModalTabSelecaoProps>) {
  const { path, perguntasFalhasNoMChat, onClose } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value: selectionMode, onChange: setSelectionMode },
  } = useField(resolveName(path.selectionMode), { subscription: { value: true } })

  const toggleMode = () => {
    setSelectionMode(
      selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
        ? ConsultaSeguimentoSelectionMode.MANUAL
        : ConsultaSeguimentoSelectionMode.AUTOMATICO
    )
  }

  return (
    <VFlow vSpacing={2}>
      <HFlow alignItems='flex-start' justifyContent='flex-end'>
        <MChatRfModalButtonClose onClose={onClose} />
      </HFlow>
      <Heading level={1}>Entrevista de seguimento</Heading>
      <VFlow>
        <Text>
          <strong>
            Na Entrevista de Seguimento é necessário reaplicar as perguntas nas quais a criança inicialmente falhou no
            M-Chat-R.
          </strong>{' '}
          Cada página da entrevista corresponde a uma pergunta do M-CHAT-R.{' '}
        </Text>
        <Box style={styles.box}>
          <VFlow vSpacing={0}>
            <HFlow alignItems='center' justifyContent='space-between' style={styles.header}>
              <HFlow alignItems='center' hSpacing={0.5}>
                <Text fontWeight='bold'>Perguntas do M-CHAT-R nas quais a criança falhou</Text>
                {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO && (
                  <Tooltip text='As perguntas do M-CHAT-R nas quais a criança falhou foram selecionadas automaticamente.'>
                    <PecTag icon='sparkles' style={styles.tag}>
                      Automático
                    </PecTag>
                  </Tooltip>
                )}
              </HFlow>
              <Button
                kind={selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO ? 'primary' : 'normal'}
                skin='outline'
                size='small'
                onClick={toggleMode}
              >
                <Icon
                  icon={selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO ? 'penOutline' : 'redo'}
                  style={styles.buttonIcon}
                />
                {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
                  ? 'Editar seleção'
                  : 'Restaurar seleção automática'}
              </Button>
            </HFlow>
            {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO && (
              <VFlow style={styles.list}>
                {perguntasFalhasNoMChat.map((pergunta) => (
                  <Text key={pergunta}>
                    {getMChatPerguntaNumber(pergunta)}. “{MCHAT_PERGUNTAS[pergunta]}”
                  </Text>
                ))}
              </VFlow>
            )}
            {selectionMode === ConsultaSeguimentoSelectionMode.MANUAL && (
              <VFlow style={styles.list}>
                {Object.keys(MCHAT_PERGUNTAS).map((pergunta: MChatPerguntaEnum) => (
                  <HFlow key={pergunta} alignItems='center' justifyContent='space-between' hSpacing={2}>
                    <Text>
                      {getMChatPerguntaNumber(pergunta)}. “{MCHAT_PERGUNTAS[pergunta]}”
                      {perguntasFalhasNoMChat.includes(pergunta) && (
                        <span css={styles.tagFalhou}>
                          <span css={styles.tagFalhouIconWrapper}>
                            <Icon size={1} icon='infoCircleOutline' />
                          </span>
                          <Text color='inherit'>Falhou no M-Chat-R</Text>
                        </span>
                      )}
                    </Text>
                    <CheckboxField name={path.perguntasSelecionadas} value={pergunta} />
                  </HFlow>
                ))}
              </VFlow>
            )}
          </VFlow>
        </Box>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  tag: css`
    color: ${theme.pallete.primary.main};
    background: ${theme.pallete.primary.c90};
  `,
  buttonIcon: css`
    margin-right: 0.25rem;
  `,
  box: css`
    padding: 0;
  `,
  header: css`
    padding: 0.5rem 1rem;
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  list: css`
    & > div {
      border-bottom: 1px solid ${theme.pallete.divider};
      margin: 0;
      padding: 0.25rem 1rem;
      &:last-child {
        border-bottom: 0;
      }
    }
  `,
  tagFalhou: css`
    color: ${theme.pallete.primary.main};
    margin-left: 0.25rem;
  `,
  tagFalhouIconWrapper: css`
    margin-right: 0.25rem;
    svg {
      margin-bottom: -0.25rem;
    }
  `,
})
